import { useCallback, useEffect, useState } from 'react';
import SumsubWebSdk from '@sumsub/websdk-react';
import type { AnyEventName, EventPayload } from '@sumsub/websdk';

import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { KycSdkSkeleton } from './KycSdkSkeleton';
import { KycSignupHandoff } from './KycSignupHandoff';
import { KycInProgressPanel } from './panels/KycInProgressPanel';
import { useAppAuth } from '@/lib/useAppAuth';
import { useKycActionToken, useKycStatus } from '@/lib/kyc/useKyc';
import { useKycSupportAction } from '@/lib/kyc/useKycSupportAction';
import { useKycActiveActionId, useKycActionOpenToken, useCloseKycActionGate } from '@/store/kycActionGateStore';
import { clientErrorReporter } from '@/lib/realtime/client-error-reporter';

// SnsError isn't re-exported from the package root; mirror the shape we read.
interface SnsError {
  code?: string;
  error?: string;
  reason?: string;
}

const GENERIC_LOAD_ERROR = "We couldn't start this verification. Please try again.";
const STATUS_POLL_INTERVAL_MS = 3000;

/**
 * App-wide popup for independent **action verifications** (extra checks, e.g.
 * admin-triggered). Mount once in the app shell next to <KycGate />. Opens when
 * something sets an active action on the action gate store — the Settings list
 * (manual) or the proactive `useKycActionAutoGate` (on refresh / realtime). Runs
 * the check in a Sumsub WebSDK modal and watches `status.actions`; never touches
 * identity. Renders nothing while no action is active.
 */
export function KycActionGate() {
  const externalActionId = useKycActiveActionId();
  const openToken = useKycActionOpenToken();
  const close = useCloseKycActionGate();
  const { user, isOnboarded } = useAppAuth();
  // Same `['kyc','status']` cache the list/auto-gate read — just for the title.
  const { data } = useKycStatus(!!externalActionId);

  if (!externalActionId) return null;
  // Signup completion comes before action checks — same ordering as KycGate;
  // the server enforces it on the token mint (403 SIGNUP_INCOMPLETE).
  if (user && !isOnboarded) return <KycSignupHandoff onClose={close} />;
  const displayName = data?.actions?.find((a) => a.externalActionId === externalActionId)?.displayName ?? 'Verification';

  // Remount the runner whenever a new open is requested so its token / error
  // state starts clean even if the same action re-opens.
  return (
    <ActionRunner
      key={`${externalActionId}:${openToken}`}
      externalActionId={externalActionId}
      displayName={displayName}
      onClose={close}
      onApproved={close}
    />
  );
}

type Phase = 'loading' | 'ready' | 'error' | 'processing';

/**
 * Runs a single action check in the Sumsub WebSDK. Mirrors the identity gate's
 * runner (KycGate) but mints an action-scoped token and watches `status.actions`
 * for this action turning APPROVED.
 */
function ActionRunner({
  externalActionId,
  displayName,
  onClose,
  onApproved,
}: {
  externalActionId: string;
  displayName: string;
  onClose: () => void;
  onApproved: () => void;
}) {
  const openSupport = useKycSupportAction();
  const { data, isError, isFetching, refetch } = useKycActionToken(externalActionId);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [reviewPassed, setReviewPassed] = useState(false);
  // The SDK has posted `idCheck.onReady` — its UI is rendered inside the
  // iframe. Until then we show our own skeleton over a collapsed (but
  // mounted) iframe, so the user never sees Sumsub's internal spinner.
  const [sdkReady, setSdkReady] = useState(false);
  const { data: statusData, refetch: refetchStatus } = useKycStatus(
    true,
    reviewPassed ? STATUS_POLL_INTERVAL_MS : undefined,
  );

  const approved =
    statusData?.actions?.some((a) => a.externalActionId === externalActionId && a.status === 'APPROVED') ?? false;
  useEffect(() => {
    if (approved) onApproved();
  }, [approved, onApproved]);

  const token = data?.token ?? null;
  const phase: Phase = reviewPassed ? 'processing' : sdkError || isError ? 'error' : token ? 'ready' : 'loading';
  const errorMessage = sdkError ?? GENERIC_LOAD_ERROR;

  useEffect(() => {
    if (isError) clientErrorReporter.report({ kind: 'kyc-token-failed', detail: { externalActionId } });
  }, [isError, externalActionId]);

  const retry = useCallback(() => {
    setSdkError(null);
    void refetch();
  }, [refetch]);

  const expirationHandler = useCallback(async (): Promise<string> => {
    const result = await refetch();
    const fresh = result.data?.token;
    if (!fresh) {
      setSdkError(GENERIC_LOAD_ERROR);
      throw new Error('failed to refresh action token');
    }
    return fresh;
  }, [refetch]);

  const handleError = useCallback(
    (error: SnsError) => {
      if (error?.reason === 'permissionsDenied') {
        return;
      }
      clientErrorReporter.report({
        kind: 'kyc-websdk-error',
        detail: { externalActionId, code: error?.code, error: error?.error, reason: error?.reason },
      });
    },
    [externalActionId],
  );

  const handleMessage = useCallback(
    (type: AnyEventName, payload: EventPayload<AnyEventName>) => {
      if (type === 'idCheck.onReady') {
        setSdkReady(true);
        return;
      }
      if (type === 'idCheck.onApplicantStatusChanged' || type === 'idCheck.applicantReviewComplete') {
        void refetchStatus();
        const reviewAnswer = (payload as { reviewResult?: { reviewAnswer?: string } })?.reviewResult?.reviewAnswer;
        if (reviewAnswer === 'GREEN') setReviewPassed(true);
      }
    },
    [refetchStatus],
  );

  // The transient finalizing state (client-side GREEN, backend webhook not yet
  // landed) shows the shared branded "Verification in Progress" pop-up, with copy
  // scoped to an action check rather than identity. The status poll keeps running
  // underneath — if the check resolves APPROVED before the player taps "Got It",
  // the runner closes via onApproved; otherwise "Got It" dismisses.
  if (phase === 'processing')
    return (
      <KycInProgressPanel
        onClose={onClose}
        onContactSupport={openSupport}
        description="This check is being reviewed. We'll notify you when it's complete."
        testIdBase="kyc-action-processing"
      />
    );

  return (
    <Dialog open onClose={onClose} size="md">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">{displayName}</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-primary">
            ✕
          </button>
        </div>

        {/* One continuous skeleton from token mint through SDK boot — swapped
            for the iframe only once the SDK posts `idCheck.onReady`. */}
        {(phase === 'loading' || (phase === 'ready' && !sdkReady)) && <KycSdkSkeleton />}

        {phase === 'error' && (
          <div className="flex flex-col gap-3 py-4" role="alert" data-testid="kyc-action-error">
            <p className="text-sm text-danger-alt">{errorMessage}</p>
            <div className="flex gap-2">
              <Button onClick={retry} disabled={isFetching}>
                {isFetching ? 'Retrying…' : 'Try again'}
              </Button>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {phase === 'ready' && token && (
          // Collapsed (invisible + max-h-0, not display:none, so the iframe
          // still boots and measures) until `idCheck.onReady`; the skeleton
          // above stands in for Sumsub's internal spinner.
          <div
            data-testid="kyc-action-sdk-host"
            // -mt-4 cancels the parent's gap-4 so the collapsed host is zero-footprint.
            className={cn(!sdkReady && 'invisible max-h-0 -mt-4 overflow-hidden')}
          >
            <SumsubWebSdk
              accessToken={token}
              expirationHandler={expirationHandler}
              onMessage={handleMessage}
              onError={handleError}
              config={{}}
              options={{ addViewportTag: false, adaptIframeHeight: true }}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
